<template>
  <div
    class="max-w-screen-2xl mx-auto w-full pb-6"
    data-test-id="merchant-details-container"
  >
    <div class="space-y-4">
      <!-- Header Section -->
      <div
        class="bg-surface-0 dark:bg-surface-900 shadow-sm rounded-lg p-6 overflow-hidden"
        data-test-id="merchant-details-header"
      >
        <div
          class="flex flex-col lg:flex-row items-center justify-between gap-2"
        >
          <div>
            <h3 class="text-surface-600 dark:text-surface-300 mt-1 text-2xl">
              {{ merchant?.MERCHANT_NAME || 'Loading...' }}
            </h3>
          </div>
          <div
            class="flex items-center space-x-3 w-full lg:w-auto"
            data-test-id="merchant-details-header-actions"
          >
            <Button
              label="Go Back"
              icon="pi pi-arrow-left"
              @click="$router.back()"
              severity="primary"
              variant="outlined"
              size="small"
              data-test-id="merchant-details-back-btn"
            />
            <Button
              label="Edit tolerance"
              icon="pi pi-pencil"
              size="small"
              v-if="canEdit"
              @click="openEditTolerance"
              data-test-id="merchant-details-edit-tolerance-btn"
            />
            <Button
              label="Edit Reserves"
              icon="pi pi-sliders-h"
              size="small"
              v-if="canEdit"
              @click="openEditReserves"
              data-test-id="merchant-details-edit-reserves-btn"
            />
            <Button
              label="Manage Settlements"
              icon="pi pi-credit-card"
              size="small"
              v-if="canManageSettlements"
              @click="openManageSettlements"
              data-test-id="merchant-details-manage-settlements-btn"
            />
          </div>
        </div>
      </div>

      <!-- Overview Section - Always Visible -->
      <div class="" data-test-id="merchant-details-overview">
        <MerchantOverview :merchant="merchant" :is-loading="isLoading" />
      </div>

      <!-- Tab Navigation -->
      <div
        class="bg-surface-0 dark:bg-surface-900 shadow-sm rounded-lg overflow-hidden"
        data-test-id="merchant-details-tabs"
      >
        <Tabs
          v-model:value="activeTab"
          class="p-0"
          :scrollable="true"
          data-test-id="merchant-details-tabs-component"
        >
          <TabList>
            <Tab
              value="overview"
              class="px-4 sm:px-6 py-4 text-sm sm:text-base"
              data-test-id="merchant-details-tab-overview"
            >
              Overview
            </Tab>
            <Tab
              value="alerts"
              class="px-4 sm:px-6 py-4 text-sm sm:text-base"
              data-test-id="merchant-details-tab-alerts"
            >
              Alerts<span
                v-if="activeAlertCount > 0"
                class="ml-1 text-surface-500 dark:text-surface-400 tabular-nums"
                >({{ activeAlertCount }})</span
              >
            </Tab>
            <Tab
              value="events"
              class="px-4 sm:px-6 py-4 text-sm sm:text-base"
              data-test-id="merchant-details-tab-events"
            >
              Current Events
            </Tab>
            <Tab
              v-if="canEdit"
              value="cases"
              class="px-4 sm:px-6 py-4 text-sm sm:text-base"
              data-test-id="merchant-details-tab-cases"
            >
              Cases<span
                v-if="openCaseCount > 0"
                class="ml-1 text-surface-500 dark:text-surface-400 tabular-nums"
                >({{ openCaseCount }})</span
              >
            </Tab>
          </TabList>

          <!-- Tab Panels -->
          <TabPanels>
            <!-- Overview Panel-->
            <TabPanel
              value="overview"
              data-test-id="merchant-details-overview-tab-panel"
            >
              <div
                class="grid grid-cols-1 sm:grid-cols-2 min-[1440px]:grid-cols-[minmax(0,1fr)_320px] gap-4"
                data-test-id="merchant-details-analytics"
              >
                <MerchantAnalytics v-if="merchant" :merchant="merchant" />
                <aside
                  class="min-[1440px]:col-start-2 min-[1440px]:row-start-1 min-[1440px]:sticky min-[1440px]:top-4 min-[1440px]:self-start min-[1440px]:max-h-[calc(100vh-2rem)] min-[1440px]:overflow-y-auto"
                >
                  <MerchantAlertsSidebar
                    v-if="merchant"
                    @switch-tab="activeTab = $event"
                    @action-completed="handleAlertActionCompleted"
                  />
                </aside>
                <aside v-if="merchant" class="min-[1440px]:col-span-full">
                  <MerchantCasesSidebar
                    :merchant-id="merchant.MERCHANT_ID"
                    @switch-tab="activeTab = $event"
                  />
                </aside>
              </div>
            </TabPanel>
            <!-- Alerts Panel -->
            <TabPanel
              value="alerts"
              data-test-id="merchant-details-alerts-tab-panel"
            >
              <div class="">
                <AlertsView
                  :merchantId="merchant?.MERCHANT_ID"
                  :showMerchantColumn="false"
                  :showHistoryToggle="true"
                />
              </div>
            </TabPanel>

            <!-- Current Events Panel -->
            <TabPanel
              value="events"
              data-test-id="merchant-details-events-tab-panel"
            >
              <div class="">
                <AlertsView
                  :merchantId="merchant?.MERCHANT_ID"
                  :showMerchantColumn="false"
                  :isAlertHistoryData="false"
                  :isCurrentEventsData="true"
                />
              </div>
            </TabPanel>

            <!-- Cases Panel -->
            <TabPanel
              v-if="canEdit"
              value="cases"
              data-test-id="merchant-details-cases-tab-panel"
            >
              <div class="">
                <MerchantCasesView :merchantId="merchant?.MERCHANT_ID" />
              </div>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </div>

      <!-- Supplier Notes. Sits below the tabs, outside TabPanels, so it stays
           visible whichever tab is open — a persistent panel rather than a tab
           of its own. -->
      <SupplierNotesPanel
        :merchant-id="supplierNotesMerchantId"
        :is-loading="isLoading"
      />
    </div>

    <EditToleranceModal />
    <EditReservesModal />
    <ManageSettlementsModal />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import { useFrontegg } from '@frontegg/vue'
import { Button, Tabs, TabList, Tab, TabPanels, TabPanel } from 'primevue'
import {
  useFCCMerchantStore,
  useFCCAlertStore,
  useFCCCaseStore,
} from '@/stores/fcc'
import { errorToastWithDetails } from '@/util/toasts.js'
import { normalizeError } from '@/util/errorNormalizer'
import { resolveVendorName } from '@/util/fcc/vendorNameOverride'
import EditToleranceModal from '@/components/fcc/merchantdetails/EditToleranceModal.vue'
import EditReservesModal from '@/components/fcc/merchantdetails/EditReservesModal.vue'
import ManageSettlementsModal from '@/components/fcc/merchantdetails/ManageSettlementsModal.vue'
import MerchantOverview from '@/components/fcc/merchantdetails/MerchantOverview.vue'
import SupplierNotesPanel from '@/components/fcc/merchantdetails/SupplierNotesPanel.vue'
import MerchantAnalytics from '@/components/fcc/merchantdetails/MerchantAnalytics.vue'
import MerchantAlertsSidebar from '@/components/fcc/merchantdetails/MerchantAlertsSidebar.vue'
import MerchantCasesSidebar from '@/components/fcc/merchantdetails/MerchantCasesSidebar.vue'
import AlertsView from '@/views/fcc/AlertsView.vue'
import MerchantCasesView from '@/components/fcc/cases/MerchantCasesView.vue'
import { usePerms } from '@/composables/usePerms'
import { PERMISSIONS } from '@/config/permissions'

const route = useRoute()
const router = useRouter()
const toast = useToast()
const frontegg = useFrontegg()
const alertStore = useFCCAlertStore()
const caseStore = useFCCCaseStore()

const tabLoading = ref(false)
const activeTab = ref('overview')

const merchantStore = useFCCMerchantStore()
const { hasPerms } = usePerms()

const merchantId = computed(() => route.params.id as string)
const merchant = computed(() => merchantStore.currentMerchant)
const isLoading = computed(() => merchantStore.loading)

// The notes panel keys off the FCC MERCHANT_ID as a string; null while the
// merchant is still loading, which the panel treats as "nothing to fetch yet".
const supplierNotesMerchantId = computed(() =>
  merchant.value ? String(merchant.value.MERCHANT_ID) : null,
)

const canEdit = computed(() => hasPerms(PERMISSIONS.FCC_UPDATE))
const canManageSettlements = computed(() =>
  hasPerms(PERMISSIONS.NETSUITE_SETTLEMENT_UPDATE),
)

const activeAlertCount = computed(() => alertStore.alerts.length)
const openCaseCount = computed(() => {
  const id = merchant.value?.MERCHANT_ID
  if (!id) return 0
  return caseStore.cases.filter(
    c =>
      c.MERCHANT_ID === id && c.JIRA_ISSUE?.IS_OPEN !== false && !c.RESOLVED_AT,
  ).length
})
const loadMerchant = async () => {
  if (!merchantId.value) {
    console.warn('No merchant ID provided, redirecting to alerts')
    router.push('/fraud/alerts')
    return
  }

  try {
    const id = Number(merchantId.value)
    if (isNaN(id)) {
      console.warn(
        `Invalid merchant ID: ${merchantId.value}, redirecting to alerts`,
      )
      router.push('/fraud/alerts')
      return
    }

    // Set current merchant in store for tab synchronization
    merchantStore.setCurrentMerchantId(id)

    // Fetch merchant details
    await merchantStore.fetchMerchantDetails(frontegg.authState, id)

    //update route meta
    if (merchant.value?.MERCHANT_NAME) {
      router.currentRoute.value.meta.title = `Merchant: ${merchant.value.MERCHANT_NAME}`
      router.currentRoute.value.meta.description = `Details and analytics for merchant ${merchant.value.MERCHANT_NAME}`
    }
    loadMerchantData()
  } catch (error) {
    console.error('Error loading merchant:', error)
    const apiError = normalizeError(error)
    errorToastWithDetails(toast, 'Error', apiError.toToastMessage(), apiError)
    router.push('/fraud/alerts')
  }
}

// non-blocking calls to fetch merchant data
const loadMerchantData = () => {
  if (!merchant.value?.MERCHANT_ID) return

  try {
    tabLoading.value = true

    // get alert history (this will be used by both history and events tabs)
    merchantStore
      .fetchMerchantAlertHistory(frontegg.authState, merchant.value.MERCHANT_ID)
      .catch(error => {
        console.error('Error fetching merchant alert history:', error)
      })

    // get event tolerances for tolerance update modal
    merchantStore
      .fetchMerchantEventTolerances(
        frontegg.authState,
        merchant.value.MERCHANT_ID,
      )
      .catch(error => {
        console.error('Error fetching merchant event tolerances:', error)
      })

    // get reserve metrics for the Reserve Metrics section
    merchantStore
      .fetchMerchantReserveMetrics(
        frontegg.authState,
        merchant.value.MERCHANT_ID,
      )
      .catch(error => {
        console.error('Error fetching merchant reserve metrics:', error)
      })

    // get the NetSuite settlement-hold flag for the Manage Settlements dialog
    merchantStore.clearVendorHoldPayment()
    const vendorName = resolveVendorName(merchant.value)
    if (vendorName && hasPerms(PERMISSIONS.NETSUITE_SETTLEMENT_READ)) {
      merchantStore
        .fetchVendorHoldPayment(frontegg.authState, vendorName)
        .catch(error => {
          console.error('Error fetching vendor hold payment:', error)
        })
    }

    // get active alerts for overview sidebar + alerts tab
    alertStore
      .fetchAlerts(
        frontegg.authState,
        { merchantId: merchant.value.MERCHANT_ID },
        true,
      )
      .catch(error => {
        console.error('Error fetching merchant active alerts:', error)
      })

    // get cases for overview cases sidebar + cases tab
    if (caseStore.cases.length === 0 && !caseStore.casesLoading) {
      caseStore.fetchCases(frontegg.authState, undefined, true).catch(error => {
        console.error('Error fetching cases:', error)
      })
    }
  } catch (error) {
    console.error('Error loading merchant data:', error)
    const apiError = normalizeError(error)
    errorToastWithDetails(toast, 'Error', apiError.toToastMessage(), apiError)
  } finally {
    tabLoading.value = false
  }
}

async function handleAlertActionCompleted() {
  if (!merchant.value?.MERCHANT_ID) return
  try {
    await Promise.all([
      alertStore.fetchAlerts(
        frontegg.authState,
        { merchantId: merchant.value.MERCHANT_ID },
        true,
      ),
      merchantStore.fetchMerchantAlertHistory(
        frontegg.authState,
        merchant.value.MERCHANT_ID,
      ),
    ])
  } catch (error) {
    console.error('Error refreshing alerts after action:', error)
  }
}

function openEditTolerance() {
  if (!merchant.value?.MERCHANT_ID) return

  merchantStore.setIsEditModalOpen(true)
  merchantStore.setCurrentMerchantId(merchant.value.MERCHANT_ID)
  merchantStore.setMerchantEventTolerance(
    merchant.value.TOLERANCE_ID || 'Medium',
  )
}

function openEditReserves() {
  if (!merchant.value?.MERCHANT_ID) return

  merchantStore.setIsEditReservesModalOpen(true)
  merchantStore.setCurrentMerchantId(merchant.value.MERCHANT_ID)
}

function openManageSettlements() {
  if (!merchant.value?.MERCHANT_ID) return

  merchantStore.setIsManageSettlementsModalOpen(true)
  merchantStore.setCurrentMerchantId(merchant.value.MERCHANT_ID)
}

onMounted(() => {
  if (!hasPerms(PERMISSIONS.FCC_READ)) return
  loadMerchant()
})

onUnmounted(() => {
  // Clear current merchant context so other views (e.g., Alerts) are not tied to this merchant
  merchantStore.setCurrentMerchantId(null as unknown as number)
  // Reset alert store filters and clear merchant-specific alerts
  alertStore.resetFilters()
  alertStore.clearAlerts()
  // Clear merchant alert history
  merchantStore.clearMerchantAlertHistory()
  // Clear reserve metrics so the next merchant doesn't flash stale numbers
  merchantStore.clearReserveMetrics()
  // Same for the settlement-hold flag
  merchantStore.clearVendorHoldPayment()
})

// Watch for route changes
watch(
  () => route.params.id,
  newId => {
    if (newId && newId !== merchantId.value) {
      loadMerchant()
    }
  },
)
</script>

<style scoped></style>
